import { AfterViewInit, Component, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MessengerService } from './services/messenger/messenger.service';
import { AnalyticsService } from './services/analytics/analytics.service';
import { NavigationEnd, Router } from '@angular/router';
import { AssistanceService } from './services/assistance/assistance.service';
import { registerLocaleData } from '@angular/common';
import localeDe from '@angular/common/locales/de';
import browser from 'browser-detect';
import { OnlineStatusService, OnlineStatusType } from 'ngx-online-status';
import { filter } from 'rxjs/operators';
import { ContentService } from './services/content/content.service';
import { IntervalService } from './services/interval/interval.service';
import { MagnifyingGlassComponent } from './components/magnifying-glass/magnifying-glass.component';
import { LocalStorageService } from './services/local-storage/local-storage.service';
import { MetaService } from './services/meta/meta.service';
import { IdleService } from './services/idle/idle.service';
import { PopupService } from './services/popup/popup.service';
import { GhosthandComponent } from './components/ghosthand/ghosthand.component';
import { NavigationItemService } from './services/navigation-item/navigation-item.service'

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  providers: [LocalStorageService]
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild(MagnifyingGlassComponent) magnifier: MagnifyingGlassComponent;
  @ViewChild(GhosthandComponent) ghosthand: GhosthandComponent;
  @ViewChild('mainApp') mainApp;

  private resurrectionCounter = 0;
  private recoveryCounter = 0;
  ready = false;
  serverError = false;
  subscriptions = [];
  overlayVisible = false;
  lastPath: string;
  analyticsSubscription: any;

  // ghost hand
  handInterval;
  systemClick = false;
  intervalIsRunning = false;

  isCorrectBrowser = true;
  isPreview = false;
  incorrectBrowserMessage = 'Wir empfehlen die Verwendung des Edge- oder Chrome-Browsers';

  noNetwork = false;

  showPreviewMsg = false;
  previewMsg = 'Sie befinden sich im Preview Modus';

  fallbackTimeout;
  fallbackInterval = 60000;
  isSingleAppUsage = false;
  private offlineTimer: any = null;

  private launchers = ['default', 'infoscreen', 'hybrid', 'pages'];
  public launcherType;

  constructor(
    private messenger: MessengerService, private analyticsService: AnalyticsService, private router: Router, private cs: ContentService,
    private assistance: AssistanceService, private onlineStatusService: OnlineStatusService, private idle: IdleService,
    private meta: MetaService, private navigationItemService: NavigationItemService
  ) {
    this.subscriptions.push(this.meta.$changed.subscribe(changed => {
      this.launcherType = this.launchers.includes(this.meta.get('ibexTouch.launcherType')) ? this.meta.get('ibexTouch.launcherType') : 'default';
    }));
    this.subscriptions.push(this.onlineStatusService.status.subscribe((status: OnlineStatusType) => {
      if (status) {
        if (this.offlineTimer) {
          clearTimeout(this.offlineTimer);
          this.offlineTimer = null;
        }

        if (this.noNetwork) {
          this.noNetwork = false;

          const now = Date.now();
          const lastReload = Number(localStorage.getItem('lastNetworkReload') || '0');

          if (now - lastReload > 5 * 60 * 1000) {
            localStorage.setItem('lastNetworkReload', now.toString());
            window.location.reload();
          }
        }
      } else {
        if (!this.noNetwork && !this.offlineTimer) {
          this.offlineTimer = setTimeout(() => {
            this.noNetwork = true;
            this.offlineTimer = null;
          }, 1000 * 30);
        }
      }
    }));
    this.subscriptions.push(this.idle.$idle.pipe(filter(Boolean)).subscribe(() => {
      this.idle.stopTimeout();
      let slides = [];
      if (this.messenger?.mainPackage?.screensaver?.entry) {
        if (this.cs.getEntry(this.messenger.mainPackage.screensaver.entry)?.related?.entries) {
          for (let related of this.cs.getEntry(this.messenger.mainPackage.screensaver.entry).related.entries) {
            if (this.cs.getEntry(related.id)?.detail?.slides && this.cs.getEntry(related.id)?.detail?.isScreensaver) {
              slides = slides.concat(this.cs.getEntry(related.id).detail.slides);
            }
          }
          if (!slides?.length) {
            this.idle.restartTimeout();
            return;
          }
        } else {
          this.idle.restartTimeout();
          return;
        }
      } else if (!!this.meta.getBoolean('ibexTouch.useGhosthand') && this.router.url.endsWith('home')) {
        this.ghosthand.start();
      } else {
        this.idle.restartTimeout();
        return;
      }
      if (slides?.length) {
        if (this.isSingleAppUsage) {
          this.router.navigate([{ outlets: { 'overlay': null } }]).then(() => {
            this.router.navigate(['home', { outlets: { 'overlay': ['screensaver'] } }]).then(() => {
            });
          });
        } else {
          this.router.navigate(['home']).then(() => {
            this.router.navigate([{ outlets: { 'overlay': null } }]).then(() => {
              this.router.navigate(['home', { outlets: { 'overlay': ['screensaver'] } }]).then(() => {
              });
            });
          });
        }
      }
    }));

    this.subscriptions.push(this.meta.$changed.subscribe(changed => {
      this.isSingleAppUsage = this.meta.getBoolean('ibexTouch.isSingleAppUsage');
      const bg = this.mainApp && this.mainApp.nativeElement && this.mainApp.nativeElement.style ? this.mainApp.nativeElement.style.backgroundImage : null;
      this.meta.getBackgroundImage('clear', bg).then(res => {
        this.mainApp.nativeElement.style.backgroundImage = res;
      });
    }));

    this.subscriptions.push(router.events.subscribe(() => {
      this.overlayVisible = router.url.indexOf('overlay:') > -1;
    }));

    this.subscriptions.push(this.messenger.$languageReady.subscribe(done => {
      if (done) {
        this.initializeApp(); // only first time
      }
    }));
    this.subscriptions.push(this.messenger.$serverError.subscribe(err => {
      this.serverError = err;
    }));
    this.subscriptions.push(this.assistance.$magnifyingGlassActive.subscribe(active => {
      this.magnifier?.[active ? 'show' : 'hide']();
    }));
    this.subscriptions.push(this.assistance.$highContrastModeActive.subscribe(active => {
      document.body.classList.toggle('high-contrast', active);
    }));

    const result = browser();
    if (result.name !== 'chrome' && result.name !== 'edge' && result.name !== 'crios' && result.name !== 'safari') {
      this.isCorrectBrowser = false;
    }

    if (window['preview']) {
      this.isPreview = true;
      this.startPreviewInterval();
    }
    IntervalService.set('resurrection', () => {
      const loaderPresent = !!document.getElementsByTagName('loader')?.length && router.url.indexOf('overlay:screensaver') === -1;
      if (loaderPresent) {
        this.resurrectionCounter = (this.resurrectionCounter || 0) + 1;
        this.recoveryCounter = 0;
      } else {
        this.recoveryCounter = (this.recoveryCounter || 0) + 1;
        this.resurrectionCounter = 0;
      }
      if (this.recoveryCounter > 120) {
        localStorage.removeItem('resurrectionMeta');
      }
      if (this.resurrectionCounter > 120) {
        const now = Date.now();
        const metaRaw = localStorage.getItem('resurrectionMeta');
        const meta = metaRaw ? JSON.parse(metaRaw) : { count: 0, last: 0 };

        const retryDelayMinutes = Math.min(60, Math.pow(2, meta.count) * 2); // 2, 4, 8, ... 60 max
        const retryDelayMs = retryDelayMinutes * 60 * 1000;

        if (now - meta.last > retryDelayMs) {
          localStorage.setItem('resurrectionMeta', JSON.stringify({ count: meta.count + 1, last: now }));
          window.location.reload();
        } else {
          console.warn(`Resurrection delayed: waiting ${Math.ceil((retryDelayMs - (now - meta.last)) / 1000)} more seconds`);
        }
      }
    }, 1000);
  }

  public ngOnInit() {
    registerLocaleData(localeDe, 'de');
  }

  ngOnDestroy() {
    for (let subscription of this.subscriptions) {
      subscription.unsubscribe();
    }
    this.analyticsSubscription?.unsubscribe?.();
    this.subscriptions.length = 0;
  }

  startPreviewInterval() {
    this.showPreviewMsg = false;
    IntervalService.set('previewMode', () => {
      this.showPreviewMsg = true;
    }, 300000);
  }

  ngAfterViewInit() {
  }

  public initializeApp() {
    this.ready = true;
    this.messenger.$initAnalytics.subscribe(() => {
      this.initAnalytics();
    });
  }

  @HostListener('touchstart')
  onTouch() {
    this.handleClickTouch();
  }

  @HostListener('click')
  onClick() {
    this.handleClickTouch();
  }

  handleClickTouch() {
    if (this.intervalIsRunning) {
      if (!this.systemClick) {
        clearInterval(this.handInterval);
        this.intervalIsRunning = false;
        this.router.navigate(['home']).then(() => {
        });
      }
      this.systemClick = false;
    }
    if (this.lastPath && this.lastPath !== '/home' && !this.lastPath.includes('videochat')) {
      this.startFallbackTimer();
    }
  }

  startFallbackTimer() {
    clearTimeout(this.fallbackTimeout);
    let packageMeta = this.messenger.$metaPackage.getValue();
    if (packageMeta?.ibexTouch?.fallbackInterval >= 0) {
      this.fallbackInterval = packageMeta.ibexTouch.fallbackInterval;
      this.isSingleAppUsage = !!packageMeta.ibexTouch.isSingleAppUsage;
    }
    if (this.fallbackInterval > 0) {
      this.fallbackTimeout = setTimeout(() => {
        PopupService.$closeAll.next(true);
        this.assistance.reset();
        if (this.launcherType === 'pages') {
          const apps = this.navigationItemService.navigationItems.filter((element: any) => !element?.entry?.navigationItem?.widgetOnly && !element?.entry?.detail?.widgetOnly);
          if (apps.length > 0) {
            const firstApp = apps[0];
            const page = firstApp.children?.[0]?.path || firstApp.path;
            if (page && !this.router.url.includes(page)) {
              setTimeout(() => {
                this.router.navigate(['home']).then(() => {});
              }, 1000);
            }
          }
          return;
        }
        if (this.lastPath && this.lastPath !== '/home') {
          this.router.navigate([{ outlets: { 'overlay': null } }]).then(() => {
          });
          if (!this.isSingleAppUsage) {
            if (document.getElementById('appContainer')) {
              document.getElementById('appContainer').classList.remove('visible');
            }
            if (document.getElementById('rightContainer')) {
              document.getElementById('rightContainer').classList.remove('visible');
            }
            setTimeout(() => {
              this.router.navigate(['/']).then(() => {
              });
            }, 1000);
          }
        }
      }, this.fallbackInterval);
    }
  }

  stopFallbackTimer() {
    clearTimeout(this.fallbackTimeout);
  }

  private initAnalytics() {
    this.analyticsSubscription?.unsubscribe?.();
    this.analyticsSubscription = this.router.events.pipe(filter((event: any) => event instanceof NavigationEnd)).subscribe(event => {
      let url = event.url;
      if (url.indexOf('(overlay:screensaver)') > -1 || url === '/') {
        url = '/home';
      } else if (url.indexOf('(overlay:menu)') > -1) {
        url = this.lastPath;
      }
      if (this.lastPath === url) {
        return;
      }
      this.lastPath = event.url;
      this.startFallbackTimer();
      url = event && event.url ? event.url.split('?')[0] : null;
      this.analyticsService.logPageView(url);
    });
  }
}
